<template>
  <div
    ref="siteBuilder"
    :class="['page-site', {'screen-small': smallScreen}]">

    <div v-if="!isWhiteLabel" class="site-subscription">
      <div>
        <p v-if="site.subscription_trialing">
          You're currently trialing
        </p>

        <router-link
          class="btn is-primary-invert"
          :to="{name: 'site-select-plan', params: site.id}">
          <span v-if="this.site.subscription_trialing">
            Choose Plan
          </span>
          <span v-else>
            Manage Plan
          </span>
        </router-link>
      </div>
    </div>

    <div class="site-builder-wrapper" :class="{ 'tips-active': tipsActive }">
      <SiteBuilderHeader
        :siteName="siteName"
        :siteDomain="siteDomain"
        :companyId="companyId"
        :siteId="siteId"
        :tipsActive="tipsActive"
        :previewModes="previewModes"
        :activeMode="activeMode"
        :hasBackLink="hasBackLink"
        :backLink="backLink"
        @changeMode="changeMode"
        @showTips="showTips"/>

      <transition name="fade" mode="out-in">
        <div key="site-builder" class="site-builder" :class="builderClasses">
          <transition name="fade">
            <div v-if="isRendering" class="loader">
              <Sprite name="gear"/>
            </div>
          </transition>

          <div class="site-menu tier">
            <div class="inner-scroll">
              <ul class="tier-list">
                <Permissions
                  tag="li"
                  class="tier-list-item"
                  :class="`menu-item-${item.slug}`"
                  v-for="(item, index) in items"
                  :key="`item-${index}`"
                  :permissions="currentUser.permissions"
                  :requireAll="item.permission">

                  <router-link :to="item.link">
                    <div>
                      <Sprite :name="item.slug"/>
                    </div>
                    <div>
                      <span>{{item.name}}</span>
                      <p class="tier-description">{{item.description}}</p>
                    </div>
                  </router-link>

                </Permissions>
              </ul>
            </div>
          </div>

          <transition name="column-width">
            <router-view
              @changeSitePage="changeSitePage"
              @updatePage="updatePage"
              :activeMode="activeMode"/>
          </transition>

          <div class="site-overview">
            <div>
              <h2>Your Checklist</h2>
              <ul>
                <li :class="{ 'is-complete': hasLogo}">
                  <Sprite :name="overviewIcon(hasLogo)"/>
                  <p>
                    <router-link :to="{name: 'logo-settings'}">Upload your logo</router-link>
                  </p>
                </li>
                <li :class="{ 'is-complete': hasPages}">
                  <Sprite :name="overviewIcon(hasPages)"/>
                  <p>
                    <router-link :to="{name: 'add-page'}">Add pages and content</router-link>
                  </p>
                </li>
                <li v-if="!isWhiteLabel" :class="{ 'is-complete': hasSubscription}">
                  <Sprite :name="overviewIcon(hasSubscription)"/>
                  <p>
                    <router-link
                      :to="{name: 'site-select-plan', params: site.id}">
                      Choose a paid plan
                    </router-link>
                  </p>
                </li>
                <li :class="{ 'is-complete': hasDomain}">
                  <Sprite :name="overviewIcon(hasDomain)"/>
                  <p>
                    <router-link :to="{name: 'domain-settings'}">Connect your domain name</router-link>
                  </p>
                </li>
                <li :class="{ 'is-complete': hasAnalytics}">
                  <Sprite :name="overviewIcon(hasAnalytics)"/>
                  <p>
                    <router-link :to="{name: 'analytics-settings'}">Connect Google Analytics</router-link>
                  </p>
                </li>
              </ul>
            </div>
          </div>

          <div class="site-preview" :class="{ 'is-small': isLanding }">
            <Tooltip v-if="tipsActive" content="A preview of your website will be shown here. It'll load the page whilst you navigate the builder"/>

            <SitePreviewer
              :key="sitePreviewerKey"
              :pageLoading="sitePageLoading"
              :iframeSrc="iframeSrc"/>
          </div>
        </div>
      </transition>

      <div class="onboarding-link">
        <router-link
          class="btn is-tertiary"
          @click.native="hideTips"
          :to="{ name: 'onboarding-video' }">
          Quick Guide
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import confirm from '@/mixins/confirm'

import {mapState} from 'vuex'

import {
  readCompanySiteData,
  deleteCompanySiteData
} from '@/lib/sites'

import {readCompanySitePagesData} from '@/lib/pages'

import Sprite from '@/components/Sprite'
import Tooltip from '@/components/Tooltip'
import SitePreviewer from '@/components/SitePreviewer'
import SiteBuilderHeader from '@/components/SiteBuilderHeader'
import Permissions from '@/components/Permissions'

const SITE_PREVIEW_PROTOCOL = process.env.VUE_APP_SITE_PREVIEW_PROTOCOL

export default {
  name: 'Site',

  mixins: [page, pagination, confirm],

  props: {
    companyId: {
      type: String,
      required: true
    },

    siteId: {
      type: String,
      required: true
    }
  },

  components: {
    SitePreviewer,
    Sprite,
    Tooltip,
    SiteBuilderHeader,
    Permissions
  },

  data() {
    return {
      site: {},
      pages: [],
      sitePage: {},
      subscription: {},
      smallScreen: '',
      sitePreviewerKey: 'previewer-0',
      sitePageLoading: false,
      tipsActive: false,
      backLink: {},
      hasBackLink: false,
      iframeSrc: '',
      baseUrl: '',
      previewModes: ['mobile', 'tablet', 'desktop'],
      activeMode: 'mobile',
      isRendering: false
    }
  },

  methods: {
    getData() {
      readCompanySiteData(this.companyId, this.siteId).then(({data}) => {
        this.site = data

        if (!this.isWhiteLabel) {
          this.handleSubscription()
        }

        this.setSitePath()
      })

      readCompanySitePagesData(this.companyId, this.siteId, this.pagination).then(
        ({data, page}) => {
          this.pages = data
          this.pageData = page
        }
      )
    },

    handleSubscription() {
      let active = this.site.subscription_active
      let trialing = this.site.subscription_trialing

      if (!trialing && !active) {
        this.redirectToPlan()
      }
    },

    updatePage() {
      this.generatePreviewerKey()
      this.loadPageAnimation()
    },

    generatePreviewerKey() {
      let min = 1
      let max = 100
      let randomIndex = Math.floor(Math.random() * (max - min + 1)) + min
      this.sitePreviewerKey = `previewer-${randomIndex}`
    },

    setSitePath() {
      this.baseUrl = SITE_PREVIEW_PROTOCOL + this.siteDomain
      this.iframeSrc = SITE_PREVIEW_PROTOCOL + this.siteDomain
    },

    changeSitePage(path) {
      this.loadPageAnimation()
      this.iframeSrc = this.baseUrl + path
    },

    deleteSite() {
      deleteCompanySiteData(this.companyId, this.siteId).then(() => {
        this.$router.push({name: 'websites', params: {companyId: this.companyId}})
      })
    },

    changeMode(mode) {
      this.isRendering = true

      window.setTimeout(() => {
        this.activeMode = mode
      }, 333)

      window.setTimeout(() => {
        this.isRendering = false
      }, 666)
    },

    loadPageAnimation() {
      this.sitePageLoading = true
      window.setTimeout(() => {
        this.sitePageLoading = false
      }, 500)
    },

    handleResize() {
      window.addEventListener('resize', this.throttle(this.setScreenMode, 100))
    },

    throttle(fn, wait) {
      let time = Date.now()

      return () => {
        if ((time + wait - Date.now()) < 0) {
          fn()
          time = Date.now()
        }
      }
    },

    setScreenMode() {
      const largeMedia = 1200

      this.smallScreen = false

      if (window.innerWidth < largeMedia) {
        this.changeMode('mobile')
        this.smallScreen = true
      }
    },

    showTips() {
      this.tipsActive = !this.tipsActive
    },

    hideTips() {
      this.tipsActive = false
    },

    redirectToPlan() {
      this.$router.push({name: 'site-select-plan', params: {siteId: this.siteId}})
    },

    setBackLink() {
      const meta = this.$route.meta

      if (meta.backLink) {
        this.hasBackLink = true

        this.backLink = {
          name: meta.backLink,
          params: {
            companyId: this.companyId,
            siteId: this.siteId
          }
        }
      } else {
        this.hasBackLink = false
      }
    }
  },

  computed: {
    ...mapState(['currentUser']),

    title() {
      return this.siteName
    },

    isWhiteLabel() {
      return this.$store.state.whiteLabel
    },

    activeTier() {
      return this.$route.meta.tier
    },

    isLanding() {
      return this.$route.name === 'site'
    },

    hasLogo() {
      return this.site.logo_versions ? this.site.logo_versions.length > -1 : false
    },

    hasPages() {
      return this.pages.length > -1
    },

    hasSubscription() {
      return this.site.subscription_active
    },

    hasDomain() {
      return this.site.domain ? !this.site.domain.includes('pagemakerpro') : false
    },

    hasAnalytics() {
      return !!this.site.analytics_id
    },

    overviewIcon() {
      return (value) => {
        return value ? 'tick' : 'bullet'
      }
    },

    items() {
      let items = [
        {
          name: 'Your Checklist',
          description: 'Your tasks',
          slug: 'overview',
          link: {
            name: 'site',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Pages',
          description: 'Add, edit & delete pages on your site',
          slug: 'pages',
          link: {
            name: 'pages'
          },
          permission: {
            page: ['list']
          }
        },
        {
          name: 'Blog Articles',
          description: 'Add, edit & delete articles in your blog',
          slug: 'articles',
          link: {
            name: 'articles'
          },
          permission: {
            article: ['list']
          }
        },
        {
          name: 'Website Settings',
          description: 'Configure site settings, SEO and integrations',
          slug: 'settings',
          link: {
            name: 'settings'
          }
        },
        {
          name: 'Contact Details',
          description: 'Configure your contact details and social platforms',
          slug: 'contact-details',
          link: {
            name: 'contact-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          },
          permission: {
            contact: ['list']
          }
        },
        {
          name: 'Appearance',
          description: 'Configure site theme, colours and fonts',
          slug: 'appearance',
          link: {
            name: 'appearance'
          }
        }
      ]

      if (this.hasShopify) {
        items.push({
          name: 'Shopify Dashboard',
          description: 'Manage your products, collections and orders',
          slug: 'shopify',
          link: {
            name: 'outbound-link',
            params: {
              link: `https://${this.site.shopify_url}/admin`
            }
          }
        })
      }

      if (this.$store.state.whiteLabel) {
        items.push({
          name: 'Newsletter Integration',
          description: 'Integrate Mailchimp to allow visitor sign ups',
          slug: 'mailchimp',
          link: {
            name: 'newsletter-settings'
          },
          permission: {
            mailchimp: ['list']
          }
        })
      }

      if (this.hasMailchimp) {
        items.push({
          name: 'Mailchimp Dashboard',
          description: 'mailchimp desc',
          slug: 'mailchimp',
          link: {
            name: 'outbound-link',
            params: {
              link: 'https://login.mailchimp.com'
            }
          }
        })
      }

      return items
    },

    siteName() {
      return this.site.name
    },

    hasShopify() {
      return !!this.site.shopify_url
    },

    hasMailchimp() {
      return !!this.site.mailchimp_settings
    },

    siteDomain() {
      return this.site.domain
    },

    deleteMessage() {
      return `Are you sure you wish to delete the site, <strong class="name">${
        this.site.name
      }</strong>? This is irreversible and all data cannot be recovered.`
    },

    builderClasses() {
      return [
        `tier-${this.activeTier}-active`,
        `mode-${this.activeMode}-active`
      ]
    }
  },

  mounted() {
    this.getData()
    this.setBackLink()
    this.handleResize()
    this.setScreenMode()
  },

  watch: {
    $route() {
      this.setBackLink()

      if (!this.$store.state.whiteLabel) {
        this.handleSubscription()
      }
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/base/theme'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

$total-nav-height: $header-height + $nav-height
$delay: .35s
$ease: ease-in-out

.page-site
  .select-action
    @include media($media-gt-medium)
      opacity: 0.5
      transition: all .2s ease-in-out

  tr
    vertical-align: middle

    &:hover
      .select-action
        transform: translateY(0)
        opacity: 1

@include media($media-gt-large)
  .column-left-enter-active, .column-left-leave-active
    transition: all 1s ease-in-out
    left: 100%

  .column-left-enter, .column-left-leave-to
    left: 100%

  .column-width-enter-active, .column-width-leave-active
    transition: all $delay $ease
    width: 100%
    overflow: auto
    -webkit-overflow-scrolling: touch

  .column-width-enter, .column-width-leave-to
    width: 0
    overflow: hidden

.site-builder-wrapper
  min-height: calc(100vh - #{$header-height})
  display: flex
  flex-direction: column
  transition: all .3s $ease
  backface-visibility: hidden

  &.tips-active
    transform: scale(0.75) translateZ(0)
    box-shadow: none
    box-shadow: 0 30px 50px rgba(0, 0, 0, 0.15)
    border-radius: 5px

    .site-builder-header
      border-top-right-radius: 5px
      border-top-left-radius: 5px

.site-subscription
  position: absolute
  top: $header-height/2
  right: $gap-medium
  transform: translateY(-50%)

  > div
    display: flex
    align-items: center

  p
    margin: 0
    color: #fff
    font-weight: bold
    margin-right: $gap-small
    text-transform: uppercase
    font-size: 12px

.preview-modes
  display: none

  .preview-size
    background: none
    border: none
    cursor: pointer

    &.is-active
      .sprite
        opacity: 1

    &:focus
      outline: none

  @include media($media-gt-large)
    display: flex

  .sprite
    fill: #fff
    opacity: 0.25
    width: 25px
    height: 25px
    transition: opacity .2s ease-in-out

.onboarding-link
  position: fixed
  bottom: -$gap-x-large
  right: 0

.site-builder
  width: 100%
  flex: 1
  position: relative
  overflow: hidden
  border-bottom-right-radius: 5px
  border-bottom-left-radius: 5px

  .loader
    position: absolute
    left: 0
    border-radius: 5px
    background: #fff
    z-index: 10
    width: 100%
    height: 100%
    display: flex
    align-items: center
    justify-content: center

    .sprite
      animation: spin 1.5s infinite linear
      width: 50px
      height: 50px

      @include themify($themes)
        fill: themed('color-secondary')

  &.mode-mobile-active
    .tier
      width: 100%

      @include media($media-gt-medium)
        width: 50%

      @include media($media-gt-large)
        width: percentage(1/3)

    .tier-two
      left: percentage(1/3)
      width: 0
      transition: all $delay $ease

      @include media($media-gt-large)
        animation: width33 $delay $ease

    .tier-three
      width: 100%

    &.tier-2-active,
    &.tier-3-active
      .screen-small &
        .site-overview
          transform: translateX(100%)

    .site-overview
      display: none

      @include media($media-gt-medium)
        display: flex
        width: 50%

      @include media($media-gt-large)
        width: percentage(1/3)

    .site-preview
      left: percentage(1/3)
      transition: left $delay $ease

      .iframe-wrapper
        width: 50vh
        margin: auto

    &.tier-2-active
      .site-preview
        left: 50%

        @include media($media-gt-large)
          left: percentage(2/3)

      .site-help
        right: -50%

      .tier-two
        width: 100%
        left: 0

        @include media($media-gt-medium)
          left: percentage(1/2)
          width: percentage(1/2)

        @include media($media-gt-large)
          left: percentage(1/3)
          width: percentage(1/3)

    &.tier-3-active
      .site-help
        display: none

      .site-preview
        left: percentage(2/3)

      .site-menu
        width: 0
        overflow: hidden

      .tier-two
        width: 100%
        left: 0

        @include media($media-gt-medium)
          width: 50%

        @include media($media-gt-large)
          width: percentage(1/3)
          animation: width33 $delay $ease

      .tier-three
        width: 100%

        @include media($media-gt-medium)
          left: 100%

        @include media($media-gt-large)
          left: 100%

    .site-preview
      display: none

      @include media($media-gt-large)
        display: flex
        width: percentage(1/3)

  &.mode-tablet-active
    .tier
      width: 25%

    .tier-two
      left: 25%
      width: 0
      transition: all $delay $ease
      animation: width25 $delay $ease

    .tier-three
      width: 100%

    .site-overview
      width: 25%

    .site-video
      width: 100%

    .site-preview
      left: 25%
      transition: left $delay $ease

      .iframe-wrapper
        width: 65vh
        margin: auto

    &.tier-2-active
      .site-preview
        left: 50%

      .tier-two
        left: 25%
        width: 25%

    &.tier-3-active
      .site-menu
        width: 0
        overflow: hidden

      .tier-two
        left: 0
        width: 25%
        animation: width25 $delay $ease

      .tier-three
        width: 100%
        left: 100%

      .site-preview
        left: 50%

    .site-preview
      width: 50%

  &.mode-desktop-active
    .tier
      width: 25%
      transition: none

    &.tier-2-active,
    &.tier-3-active
      .site-menu
        width: 0

      .tier-two
        right: 75%
        width: 25%

    &.tier-3-active
      .tier-three
        left: 0
        width: 100%

    .tier-two
      left: auto
      right: 60%

    .tier-three
      left: 100%
      width: 100%

    .site-overview
      width: 25%

    .site-preview
      width: 75%
      right: 0
      transform: scale(0.81)
      transform-origin: 0 0
      box-shadow: none
      transition: all .5s $ease

      &.is-small
        top: 15%
        transform: scale(0.5) translateX(6%)

        &:before, &:after
          content: ''
          background: rgba(0, 0, 0, 0.03)
          border-radius: 20px
          position: absolute
          left: 30px
          width: 118%
          height: 50vh
          z-index: -1
          animation: fadeIn 1s .2s ease-in-out

        &:before
          bottom: 90vh

        &:after
          bottom: -80vh

      .preview-iframe
        width: calc(125% - 80px)
        height: calc(125% - 80px)
        position: absolute
        background: #fff
        box-shadow: 0 0 50px rgba(0, 0, 0, 0.25)

  .site-overview
    background: #fff
    position: absolute
    right: 0
    height: 100%
    display: flex
    align-items: center
    flex-direction: column
    justify-content: space-between
    padding: $gap-medium
    transition: all $delay $ease

    ul
      padding: 0

    li
      display: flex
      align-items: center

      a
        @include themify($themes)
          color: themed('color-secondary')

    h1
      margin-top: 0

    .is-complete
      text-decoration: line-through

      a
        @include themify($themes)
          color: themed('color-primary')

      .sprite
        @include themify($themes)
          fill: themed('color-primary')

    .sprite
      width: 20px
      height: 20px
      margin-right: $gap-small

      @include themify($themes)
        fill: themed('color-secondary')

  .site-preview
    position: absolute
    top: 0
    right: 0
    height: 100%
    transition: all $delay $ease
    z-index: 5
    background: #fff

  .tier-list
    padding: 0
    margin: 0

    .tier-list-item
      list-style: none
      margin: 0
      height: 105px

      span
        white-space: pre

      a
        display: flex
        align-items: center
        height: 100%

        &:hover, &.is-active
          .tier-description
            max-height: 25px
            opacity: 1

    .tier-description
      margin: 0
      font-size: 12px

      @include media($media-gt-large)
        max-height: 0
        opacity: 0
        transition: max-height .3s $ease, opacity .3s $ease, color .3s $ease
        overflow: hidden

    .sprite
      width: 30px
      height: 30px
      margin-right: $gap-small
      transition: fill .3s $ease

      @include themify($themes)
        fill: themed('color-secondary')

    a
      display: flex
      font-size: 16px
      font-family: $font-body
      padding: 0 $gap-medium
      border-bottom: 1px solid #f6f5f5
      transition: all .3s $ease

      @include themify($themes)
        color: themed('color-secondary')

      &.is-active
        font-weight: bold

        @include themify($themes)
          color: themed('color-primary')

        p
          @include themify($themes)
            color: themed('color-primary')

        .sprite
          @include themify($themes)
            fill: themed('color-primary')

.tier
  transition: all $delay $ease
  position: absolute
  top: 0
  white-space: nowrap
  height: 100%

  td
    &:first-child
      width: 100%

  .page-header
    padding: $gap-small 0
    box-shadow: none
    background: none
    top: -$gap-small
    border-bottom: none

    h1
      margin: 0
      color: #e4e4e4
      letter-spacing: 1px

  .pagination
    padding: $gap-small $gap-medium

  hr
    margin: $gap-large 0
    height: 1px
    background: rgba(0, 0, 0, 0.05)

  .table
    background: none

    button
      padding: 0

    thead
      display: none

    td
      padding: $gap-medium 0
      border-bottom: 1px solid $color-border-light
      font-size: 16px
      font-family: $font-body
      cursor: pointer

      &:first-child
        padding-left: $gap-medium

      &:last-child
        padding-right: $gap-medium

      &.is-active
        font-family: $font-bold

        @include themify($themes)
          color: themed('color-primary')

.site-menu
  background: darken(white, 1%)
  box-shadow: inset -15px 0 25px rgba(0, 0, 0, 0.05)

.site-builder
  .inner-scroll
    max-height: calc(100vh - 135px)
    overflow: auto
    -webkit-overflow-scrolling: touch

.tier-3-active
  .site-menu
    width: 60px

  .tier-two
    left: 60px

.tier-two,
.tier-three
  background: #fff

.tier-three
  .page-header
    background: #fff

.tier-content
  padding: $gap-small
  overflow: auto
  height: 100%
  white-space: normal

.helper-text
  background: #fafafa
  text-align: center
  padding: $gap-small
  border-radius: 5px

  &.no-items
    white-space: pre

  ol, ul
    padding-left: $gap-small

    li
      margin: 0 0 20px
      padding-bottom: 20px
      border-bottom: 1px solid
      border-color: $color-border

      &:last-child
        border-bottom: none
        padding-bottom: 0

  p
    margin: 0 0 $gap-small
    font-size: 12px

    &:last-child
      margin-bottom: 0

.guide-options
  display: flex
  justify-content: center

  .btn
    min-width: 100px
    text-align: center
    margin: 0 $gap-x-small

.widget-preview-wrapper
  display: none

  @include media($media-gt-large)
    display: block

.create-content
  @include media($media-gt-large)
    display: flex

    .widget-fields-wrapper
      flex-basis: percentage(1/3)

    .widget-preview-wrapper
      flex-basis: percentage(2/3)
      border-radius: 5px
      background: #f1f1f1
      position: sticky
      margin-left: $gap-large
      top: 0
      height: calc(#{$overlay-height} - #{$gap-large*2})
      display: flex
      justify-content: center
      overflow: auto

  .btn-save
    position: sticky
    border-top: 1px solid $color-border
    bottom: -$gap-large
    background: rgba(255, 255, 255, 0.9)

  .widget-hint
    font-size: 10px
    text-align: center
    margin-top: $gap-medium

    @include media($media-lt-large)
      display: none

.menu-item-overview
  @include media($media-lt-medium)
    display: none
</style>
